import React, { FormEvent } from "react";
import { Checkbox, Button, Textarea } from "@altimateai/lego";
import { InteractionRequest } from "../../types";
import { useAgentChat } from "../../hooks/useAgentChat";
import { Markdown, Stack } from "@altimateai/lego";

interface InteractionPromptProps {
  interaction: InteractionRequest;
}

enum FormState {
  INITIAL,
  SUBMITTING,
  COMPLETED,
}

export const InteractionPrompt: React.FC<InteractionPromptProps> = ({
  interaction,
}) => {
  const { sendMessage } = useAgentChat();
  const [textValue, setTextValue] = React.useState("");
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [formState, setFormState] = React.useState<FormState>(
    FormState.INITIAL,
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState(FormState.SUBMITTING);

    let value: string;
    switch (interaction.type) {
      case "text":
        value = textValue;
        break;
      case "select":
        value = selectedValues[0] || "";
        break;
      case "multiSelect":
        value = selectedValues.join(",");
        break;
      case "confirm":
        value = ((e.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement)
          ?.value;
        break;
      default:
        value = "";
    }

    try {
      await sendMessage({
        message: value,
        isResponseToPrompt: true,
      });
    } catch (error) {
      console.error("Failed to submit interaction:", error);
    } finally {
      setFormState(FormState.COMPLETED);
    }
  };

  const handleCancel = async () => {
    try {
      await sendMessage({
        message: JSON.stringify({
          interactionId: interaction.id,
          value: "cancelled",
        }),
      });
    } catch (error) {
      console.error("Failed to cancel interaction:", error);
    }
  };

  const renderInput = () => {
    switch (interaction.type) {
      case "text":
        return (
          <Textarea
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            required={interaction.required}
            aria-label="Prompt input"
          />
        );
      case "select":
      case "multiSelect":
        return (
          <div className="al-space-y-2">
            {interaction.choices?.map((choice) => (
              <label
                key={choice.id}
                className="al-flex al-items-center al-gap-2"
              >
                {" "}
                <Checkbox
                  checked={selectedValues.includes(choice.value)}
                  onCheckedChange={(checked) => {
                    if (interaction.type === "select") {
                      setSelectedValues(checked ? [choice.value] : []);
                    } else {
                      setSelectedValues((prev) =>
                        checked
                          ? [...prev, choice.value]
                          : prev.filter((v) => v !== choice.value),
                      );
                    }
                  }}
                />
                <span>{choice.label}</span>
              </label>
            ))}
          </div>
        );
      case "confirm":
        return (
          <Stack direction="row">
            <Button
              disabled={
                formState === FormState.SUBMITTING ||
                formState === FormState.COMPLETED
              }
              value="yes"
            >
              Yes
            </Button>
            <Button
              variant="outline"
              value="no"
              disabled={
                formState === FormState.SUBMITTING ||
                formState === FormState.COMPLETED
              }
            >
              No
            </Button>
          </Stack>
        );
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="al-space-y-4"
      role="region"
      aria-label="Interaction"
    >
      <div className="al-font-medium">
        <Markdown content={interaction.prompt} />
      </div>
      {renderInput()}
      {interaction.type !== "confirm" && (
        <Stack direction="row">
          <Button
            type="submit"
            disabled={
              formState === FormState.SUBMITTING ||
              formState === FormState.COMPLETED
            }
          >
            Submit
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={
              formState === FormState.SUBMITTING ||
              formState === FormState.COMPLETED
            }
          >
            Cancel
          </Button>
        </Stack>
      )}
    </form>
  );
};
